import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const POLL_INTERVAL_MS = 15 * 60 * 1000 // 15 minutes
const STORAGE_KEY = 'sf-update-reloaded-to'

/**
 * Pure helper — determines whether the tab should reload to pick up a new build.
 *
 * @param currentId   BUILD_ID baked into the running bundle.
 * @param latestId    buildId fetched from /version.json.
 * @param lastAttemptedId  sessionStorage sentinel: the last buildId we already
 *                    triggered a reload for. Guards against reload loops if
 *                    version.json is ever misconfigured.
 */
export function shouldReload(
  currentId: string,
  latestId: string,
  lastAttemptedId: string | null,
): boolean {
  if (latestId === currentId) return false          // same build, nothing to do
  if (latestId === lastAttemptedId) return false    // already reloaded for this build
  return true
}

function isUnsafeToReload(): boolean {
  // Open Radix dialog (shadcn/ui dialogs all use role="dialog" + data-state)
  if (document.querySelector('[role="dialog"][data-state="open"]')) return true
  const el = document.activeElement
  if (!el) return false
  // User is mid-type in an input or textarea
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return el.value.length > 0
  }
  // Contenteditable (rich text editors)
  if ((el as HTMLElement).isContentEditable) {
    return (el.textContent ?? '').length > 0
  }
  return false
}

/**
 * Silently reloads the running SPA when a new build is deployed.
 *
 * Polling strategy:
 *   - Once on mount
 *   - On tab becoming visible (visibilitychange → visible)
 *   - Every 15 minutes
 *
 * Reload is deferred when a dialog is open or the user is typing; it is
 * retried on the next navigation, visibility event, or interval tick.
 *
 * No-op entirely in DEV (no version.json, no stable build id).
 * Mount inside AppLayout — needs router context for useLocation().
 */
export function useAppUpdater() {
  const location = useLocation()
  const pendingBuildIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (import.meta.env.DEV) return

    async function checkForUpdate() {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const { buildId } = (await res.json()) as { buildId: string }
        const lastAttempted = sessionStorage.getItem(STORAGE_KEY)
        if (!shouldReload(__BUILD_ID__, buildId, lastAttempted)) return
        attemptReload(buildId)
      } catch {
        // Network error or parse failure — silent, will retry on next poll
      }
    }

    function attemptReload(buildId: string) {
      if (!isUnsafeToReload()) {
        sessionStorage.setItem(STORAGE_KEY, buildId)
        window.location.reload()
      } else {
        pendingBuildIdRef.current = buildId
      }
    }

    function retryPending() {
      const buildId = pendingBuildIdRef.current
      if (!buildId) return
      const lastAttempted = sessionStorage.getItem(STORAGE_KEY)
      if (!shouldReload(__BUILD_ID__, buildId, lastAttempted)) {
        pendingBuildIdRef.current = null
        return
      }
      attemptReload(buildId)
    }

    void checkForUpdate()

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      if (pendingBuildIdRef.current) retryPending()
      else void checkForUpdate()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const intervalId = setInterval(() => {
      if (pendingBuildIdRef.current) retryPending()
      else void checkForUpdate()
    }, POLL_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      clearInterval(intervalId)
    }
  }, [])

  // On each navigation: attempt any pending reload. Navigating already
  // discards in-progress form state, so reloading immediately after is safe.
  useEffect(() => {
    if (import.meta.env.DEV) return
    const buildId = pendingBuildIdRef.current
    if (!buildId) return
    const lastAttempted = sessionStorage.getItem(STORAGE_KEY)
    if (!shouldReload(__BUILD_ID__, buildId, lastAttempted)) {
      pendingBuildIdRef.current = null
      return
    }
    if (!isUnsafeToReload()) {
      sessionStorage.setItem(STORAGE_KEY, buildId)
      window.location.reload()
    }
  }, [location.pathname])
}
